/**
 * @fileoverview Schema for the story-level continuity log.
 *
 * The continuity log is a ledger of binding facts (deadlines, counts, locations,
 * promises, in-world dates, world rules, ongoing conditions) that persist across
 * chapters and need to be honoured by the writer agents. Each fact has a lifecycle:
 * it enters the story in some chapter and may later be resolved (satisfied, broken,
 * expired, or superseded) in a later chapter.
 *
 * Facts are stored in the Firestore subcollection
 * `projects/{projectId}/continuityFacts/{factId}` and rehydrated into
 * `storyStructure.continuityLog` on project load, mirroring the plot-snapshot
 * persistence pattern.
 */

import { z } from 'zod';

export const ContinuityFactKindSchema = z.enum([
  'deadline',
  'count',
  'location',
  'in-world-time',
  'promise',
  'world-rule',
  'condition',
]).describe(
  'The category of binding fact. `deadline` = something must happen by a point. ' +
  '`count` = a counter with a limit (e.g. two strikes). `location` = where someone or ' +
  'something is. `in-world-time` = the in-fiction date/time anchored to a chapter. ' +
  '`promise` = a commitment made by a character. `world-rule` = a setting rule (e.g. ' +
  'shops closed on holidays). `condition` = an ongoing state that affects scenes ' +
  '(e.g. river is frozen).',
);

export const ContinuityFactResolutionTypeSchema = z.enum([
  'satisfied',
  'broken',
  'expired',
  'superseded',
]);

export const ContinuityFactResolutionSchema = z.object({
  chapterId: z.string().describe('The chapter in which the fact was resolved.'),
  type: ContinuityFactResolutionTypeSchema.describe(
    'How the fact was resolved: satisfied (deadline met, promise kept), ' +
    'broken (deadline missed, promise broken), expired (no longer relevant), ' +
    'superseded (replaced by a newer fact).',
  ),
  note: z.string().optional().describe('Optional explanation of how the fact was resolved.'),
});

export const ContinuityFactSourceSchema = z.enum([
  'authored',
  'agent-proposed',
  'agent-confirmed',
]);

export const ContinuityFactSchema = z.object({
  id: z.string().describe('Stable unique identifier for this fact.'),
  kind: ContinuityFactKindSchema,
  summary: z.string().describe(
    'A short, agent-readable statement of the fact. Should stand on its own without ' +
    'requiring additional context — e.g. "Anna must deliver the report by May 3" ' +
    'rather than "the deadline".',
  ),
  details: z.string().optional().describe('Optional longer prose if context matters.'),

  createdAtChapterId: z.string().describe('The chapter in which this fact entered the story.'),
  activeFromChapterId: z.string().optional().describe(
    'Optional override for when the fact becomes active. Defaults to the creation chapter.',
  ),
  resolution: ContinuityFactResolutionSchema.optional().describe(
    'Set once the fact is resolved. Absence means the fact is still active.',
  ),

  // Kind-specific fields. All free-form strings — authors think in story terms
  // ("by the harvest moon"), not ISO timestamps. We capture structure where
  // it earns its keep (lifecycle, kind), text where it doesn't (the value).
  dueAt: z.string().optional().describe(
    'For deadlines: when the fact comes due, in author-readable form ("May 3", ' +
    '"by chapter 7", "before the trial"). Free-form text.',
  ),
  counter: z.object({
    current: z.number(),
    limit: z.number(),
  }).optional().describe('For counts: current value and the limit (e.g. {current: 2, limit: 3} for two strikes).'),
  locationName: z.string().optional().describe('For locations: the place name.'),
  inWorldTime: z.string().optional().describe(
    'For in-world-time anchors: the in-fiction date/time tied to this chapter ("evening of May 2").',
  ),

  source: ContinuityFactSourceSchema.describe(
    'Where the fact came from. `authored` = manually added by the user. ' +
    '`agent-proposed` = pending review. `agent-confirmed` = user has accepted the proposal.',
  ),
  proposedBy: z.string().optional().describe('Agent ID that proposed this fact, if applicable.'),
  updatedAt: z.string().describe('ISO timestamp of last update.'),
});

export type ContinuityFact = z.infer<typeof ContinuityFactSchema>;
export type ContinuityFactKind = z.infer<typeof ContinuityFactKindSchema>;
export type ContinuityFactResolution = z.infer<typeof ContinuityFactResolutionSchema>;
export type ContinuityFactResolutionType = z.infer<typeof ContinuityFactResolutionTypeSchema>;
export type ContinuityFactSource = z.infer<typeof ContinuityFactSourceSchema>;
